import { z } from 'zod';
/**
 * CER Inspections Portal wire schemas.
 *
 * These model the data the extension needs to autofill into the portal's
 * forms. We are NOT modelling HTTP payloads to CER — the backend never talks
 * to CER. These are the shapes the backend returns and the content script
 * consumes to drive form filling.
 *
 * There are two report types:
 *   - PV system inspection (cover sheet + PV hardware)
 *   - Battery system inspection (cover sheet + battery hardware)
 *
 * TODO: the field lists below are skeletons. Finalising them requires a DOM
 * inspection pass on the real portal with a sample inspection in progress —
 * see docs/FIELD_MAPPING.md.
 */
/**
 * Job identifier as it appears on the inspection record and in Dropbox folder names.
 *
 * Two prefixes in the wild:
 *   - `PVD…` — solar PV system inspections
 *   - `BTY…` — battery (BESS) inspections
 *
 * The prefix doubles as the inspection-type indicator when no other source is available.
 */
export const AccreditationCodeSchema = z
    .string()
    .regex(/^(PVD|BTY)[-\s]?\d+$/i, 'Expected a PVD or BTY job ID like "PVD1234567" or "BTY922"')
    .transform((v) => v.replace(/[\s-]/g, '').toUpperCase());
/** Which inspection workflow this job is. Drives which form the extension targets. */
export const InspectionTypeSchema = z.enum(['pv', 'battery']);
/** Derive PV vs battery from the job ID prefix. Caller may override if a more authoritative source exists. */
export function inspectionTypeFromAccreditationCode(id) {
    return id.toUpperCase().startsWith('BTY') ? 'battery' : 'pv';
}
/**
 * One of the eight ITP outcome bands. The string values are LITERAL — they match
 * the visible labels on the ITP "Inspection Result" radio group exactly,
 * including British spelling ("Practise"). The CER portal exposes a similar
 * dropdown on its Assessment page; the CER content script maps from this enum
 * to the CER portal's option text at fill time.
 */
export const InspectionOutcomeSchema = z.enum([
    'Industry Best Practise',
    'Complies',
    'Adequate',
    'Sub-standard',
    'Sub-standard - Third Party Incident',
    'Unsafe',
    'Unsafe - Product Recall',
    'Unsafe - Third Party Incident',
]);
/**
 * The canned reasons a roof cavity was not entered. Stable machine values —
 * the display wording lives in `ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS` so
 * copy edits never invalidate stored submissions.
 */
export const RoofCavityNotInspectedReasonSchema = z.enum([
    'home-owner-not-home',
    'no-consent-to-access',
    'manhole-not-safely-accessible',
    'no-roof-cavity',
]);
export const ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS = {
    'home-owner-not-home': 'Occupier not home at time of inspection',
    'no-roof-cavity': 'No roof cavity exists',
    'no-consent-to-access': 'Occupier did not provide consent to access',
    'manhole-not-safely-accessible': 'The man hole could not be accessed safely at time of the inspection',
};
/**
 * Cover-sheet fields common to both PV and battery reports, for either portal.
 *
 * Single source of truth for the editable cover-sheet inputs the extension
 * fills on either portal. Optional everywhere — sources populate what they
 * have; the content scripts fill what's present and skip what isn't.
 *
 * Field naming maps to the EDITABLE input we autofill, not to whatever the
 * portal's own labelling implies. Notable: `inspectorIdCardNumber` was
 * previously called `installerAccreditation`, which was a misnomer — both
 * portals' editable accreditation field is the INSPECTOR's CER ID card
 * number (e.g. "009CER-318B"), populated from the inspector's profile.
 */
export const InspectionCoverSheetSchema = z.object({
    relatedAccreditationCode: z.string().optional(),
    jobId: AccreditationCodeSchema,
    /** ISO date (YYYY-MM-DD). Legacy — prefer `inspectionStartedAt`/`-EndedAt`. */
    inspectionDate: z.string().optional(),
    /**
     * Inspection start time. When derived from EXIF DateTimeOriginal (the preferred path),
     * this is a NAIVE ISO string (`YYYY-MM-DDTHH:MM:SS`, no `Z`/offset) reflecting the
     * camera's local time at capture. When falling back to Dropbox file metadata it has a `Z`
     * and is UTC. Display layer should treat `Z`-tagged values as UTC and naive values as
     * customer-local. The portal field expects the customer's local time.
     */
    inspectionStartedAt: z.string().optional(),
    /** Inspection end time — same format/semantics as `inspectionStartedAt`. */
    inspectionEndedAt: z.string().optional(),
    /**
     * Original installer's name as recorded on the installation. NOT autofilled
     * today — the portals display this from their own records. Reserved for
     * verification flows.
     */
    installerName: z.string().optional(),
    /**
     * Inspector's CER ID card number, e.g. "009CER-318B". Fills the editable
     * "Inspector ID Card Number" / "Inspector Accreditation Number" input on
     * both portals. Sourced from the inspector's profile, not from the original
     * installation record.
     */
    inspectorIdCardNumber: z.string().optional(),
    updatedOwnerEmail: z.string().optional(),
    customerName: z.string().optional(),
    siteAddress: z.string().optional(),
    /**
     * Six tenderers per the meeting transcript. The exact strings each portal expects are TBD —
     * confirm via DOM inspection before treating these as canonical.
     */
    tenderer: z.enum(['Kerfoot', 'ITP', 'Smart Energy', 'MEA', 'GSES', 'Tech Safe']).optional(),
    /**
     * Whether the roof cavity was physically entered during the inspection.
     * Fills the CER cover sheet's "Roof Cavity Has Been Inspected" Yes/No
     * select AND the ITP General tab's "Roof cavity inspected" checkbox.
     * Defaults to leaving the field blank (inspector picks).
     */
    roofCavityInspected: z.boolean().optional(),
    /**
     * Why the roof cavity was NOT entered. Only meaningful when
     * `roofCavityInspected` is false; the field tool clears it when the
     * inspector flips the answer back to Yes.
     *
     * Canned reasons come from technical-head feedback (Aug 2026) — inspectors
     * were writing the same three sentences by hand. Multi-select because more
     * than one can be true on the same job (nobody home AND no safe access).
     * `roofCavityNotInspectedNote` carries anything the three don't cover.
     *
     * Neither portal has a matching input today — this rides in the submission
     * for the report writer and the QA screen.
     */
    roofCavityNotInspectedReasons: z.array(RoofCavityNotInspectedReasonSchema).optional(),
    /** Free text for a reason outside the canned three. */
    roofCavityNotInspectedNote: z.string().optional(),
    /**
     * Whether the system uses microinverters (one inverter per panel) rather
     * than a string inverter. Ticks the ITP General tab's "Microinverter
     * system" checkbox. CER doesn't expose an equivalent field; the CER
     * content script ignores this. Sourced from vision/hardware extraction
     * when available — left blank otherwise.
     */
    microinverterSystem: z.boolean().optional(),
    /* Consent-provider block ──────────────────────────────────────────────────
     *
     * The customer who signs the consent form to allow the inspection. When the
     * homeowner signs, `consentProviderIsHomeOwner` is true and the name fields
     * below are unused (the portal hides them). When a different person signs
     * — tenant, agent, partner — the name fields apply.
     *
     * On ITP the portal has a single combined `consent_provider` text input —
     * the ITP content script joins first + surname with a space when filling.
     */
    /** True when the homeowner is the consent-form signer. Ticks the CER cover-sheet checkbox. */
    consentProviderIsHomeOwner: z.boolean().optional(),
    /** Consent provider's first name, when not the homeowner. */
    consentProviderFirstName: z.string().optional(),
    /** Consent provider's surname, when not the homeowner. */
    consentProviderSurname: z.string().optional(),
    /** Consent provider's email — fills CER's "Updated Consent Provider Email Address". */
    consentProviderEmail: z.string().optional(),
    /**
     * Free-text general comments about the overall inspection. Fills ITP's
     * "General comments" rich-text box. CER doesn't have a direct equivalent;
     * the CER content script ignores this field.
     */
    generalComments: z.string().optional(),
    /**
     * Free-text inspector recommendations. Fills both ITP's "Inspector
     * Recommendation" rich-text box AND the CER Assessment page's
     * "Inspector_General_Recommendations" textarea.
     *
     * For PV inspections, the Dropbox source aggregates this from `_RECs_`-
     * tagged photo filenames (e.g. `PVD123_GENERAL_RECs_Cracked tiles`) —
     * those photos are observations rather than clause findings.
     */
    inspectorRecommendation: z.string().optional(),
    /**
     * Overall inspection outcome. Picks one of the 8 ITP bands; the CER
     * Assessment page exposes a similar dropdown. Setting this still leaves
     * the per-clause outcomes for the inspector to fill — this is the
     * top-of-report verdict only.
     *
     * Conservative default: most sources leave this blank (it's a judgement
     * call combining clause findings with contextual factors that aren't in
     * the payload).
     */
    overallOutcome: InspectionOutcomeSchema.optional(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * Hardware payloads
 *
 * Each portal/inspection-type pair has its own hardware shape. The CER and ITP
 * shapes start out aligned but are kept separate so each can diverge as the DOM
 * inspection comes in — the portals will not move in lockstep.
 *
 * TODO: every field below is provisional. Lock down via DOM inspection.
 * ──────────────────────────────────────────────────────────────────────────── */
/* PV hardware — modular by row.
 *
 * The CER InverterList and SolarPanelModuleList portal forms each accept N
 * rows; the schema models this as `inverters[]` and `modules[]`. Field names
 * here use camelCase with units suffixed where helpful (e.g. `acPowerRatingW`)
 * so the source side can ferry numbers/strings without ambiguity. The portal
 * happens to render most numeric inputs as text fields — content scripts
 * stringify at fill time.
 *
 * Most installations have a single inverter and a single module type, but
 * multi-inverter (e.g. SolaX + Sungrow on one site) and multi-module
 * (different brands across multiple roofs) systems are real. */
/** One row of the CER `InverterList[i]` form. */
export const PvInverterEntrySchema = z.object({
    make: z.string().optional(),
    model: z.string().optional(),
    /**
     * Inverter unit serial. Not rendered on CER's **PV** inverter row, but the
     * CER **battery** Hardware page does have `Inverter_Serial_Number`, and ITP
     * uses it too.
     */
    serial: z.string().optional(),
    quantity: z.number().int().nonnegative().optional(),
    /** AC continuous power rating, watts. */
    acPowerRatingW: z.number().nonnegative().optional(),
    /** AC nominal current, amperes. */
    acCurrentNominalA: z.number().nonnegative().optional(),
    /** IP rating string (e.g. "IP66"). */
    ipRating: z.string().optional(),
    dcVoltageMpptLowerV: z.number().nonnegative().optional(),
    dcVoltageMpptUpperV: z.number().nonnegative().optional(),
    dcVoltageMaximumV: z.number().nonnegative().optional(),
    dcCurrentMaximumA: z.number().nonnegative().optional(),
    /** Maximum DC input short-circuit current (Isc), amperes. Distinct from dcCurrentMaximumA. */
    dcCurrentShortCircuitA: z.number().nonnegative().optional(),
    /** True if the inverter is galvanically isolated. */
    galvanicallyIsolated: z.boolean().optional(),
});
/** One row of the CER `SolarPanelModuleList[i]` form. */
export const PvModuleEntrySchema = z.object({
    make: z.string().optional(),
    model: z.string().optional(),
    quantity: z.number().int().nonnegative().optional(),
    /** Per-panel power rating, watts. */
    powerRatingW: z.number().nonnegative().optional(),
    voltageOpenCircuitV: z.number().nonnegative().optional(),
    currentShortCircuitA: z.number().nonnegative().optional(),
    /**
     * Serial numbers of installed panels of THIS model. CER's portal has one
     * `SerialNumber` text field per module row — the content script writes the
     * first serial there; the rest stay in the payload as a record (a future
     * "add row" or "next module type" loop can consume them).
     */
    serials: z.array(z.string()).optional(),
});
/** One row of the CER AC isolator list. */
export const PvAcIsolatorEntrySchema = z.object({
    ratingA: z.number().nonnegative().optional(),
    insideSwitchboard: z.boolean().optional(),
    nextToInverter: z.boolean().optional(),
});
/** One row of the CER DC isolator list. */
export const PvDcIsolatorEntrySchema = z.object({
    voltageV: z.number().nonnegative().optional(),
    currentA: z.number().nonnegative().optional(),
    location: z.enum(['Adjacent to inverter', 'Integrated', 'Rooftop', 'Other']).optional(),
    locationOther: z.string().optional(),
    polarised: z.boolean().optional(),
});
/** PV hardware — arrays of inverter and module rows, plus system-wide totals. */
export const CerPvHardwareSchema = z.object({
    inverters: z.array(PvInverterEntrySchema).optional(),
    modules: z.array(PvModuleEntrySchema).optional(),
    acIsolators: z.array(PvAcIsolatorEntrySchema).optional(),
    dcIsolators: z.array(PvDcIsolatorEntrySchema).optional(),
    /** System-wide cumulative array Voc — fills the standalone CER `Total_Solar_Array_Voltage_Open_Circuit` input. */
    totalArrayVoltageOpenCircuitV: z.number().nonnegative().optional(),
    /** System-wide cumulative array Isc — fills the standalone CER `Total_Solar_Array_Output_Current_Short_Circuit` input. */
    totalArrayCurrentShortCircuitA: z.number().nonnegative().optional(),
    // TODO: DC cabling, earthing, mounting system...
});
/** One battery row. CER and ITP both use this shape; ITP extends below. */
export const BatteryEntrySchema = z.object({
    make: z.string().optional(),
    model: z.string().optional(),
    /**
     * Product family, e.g. "Lynx D G2". Not a portal input — carried so the
     * inspector verifying prefilled hardware sees the name printed on the unit,
     * which often matches the series rather than the model number.
     */
    series: z.string().optional(),
    /**
     * Legal manufacturer, e.g. "GoodWe Technologies Co Ltd", as distinct from the
     * consumer brand in `make` ("GOODWE"). Verification context, not a portal input.
     */
    manufacturer: z.string().optional(),
    serial: z.string().optional(),
    /** Energy capacity in kWh — answers T47 clause Integrated BESS 10 / Pre-assembled BS 11. */
    capacityKwh: z.number().nonnegative().optional(),
});
/**
 * Battery system taxonomy (AS/NZS 5139:2019). Used by ITP's T47 BESS checklist
 * and by the CER portal's `Battery_System_Type` radio, whose three options map
 * to these values 1:1.
 *   - integrated     — Pre-assembled Integrated BESS (Section 4)
 *   - pre-assembled  — Pre-assembled Battery System (Section 5)
 *   - other          — Other (Section 6)
 *
 * Declared here rather than further down because both the CER and ITP battery
 * entry schemas below reference it.
 */
export const BessSystemTypeSchema = z.enum(['integrated', 'pre-assembled', 'other']);
/** Decisive Voltage Classification under AS/NZS 5139:2019. */
export const DvcClassificationSchema = z.enum(['DVC-A', 'DVC-B', 'DVC-C']);
/**
 * One battery row as the **CER portal's** Hardware page wants it.
 *
 * Extends {@link BatteryEntrySchema} with the taxonomy fields the portal
 * actually renders. These already existed on the ITP variant
 * ({@link ItpBatteryEntrySchema}) — the CER side was a stub, so the richer
 * model sat on the schema that doesn't feed the CER portal.
 *
 * Portal targets (confirmed live 2026-08-09, BTY6303119). Which block is
 * populated depends on `systemType`:
 *
 *   systemType 'integrated'    → `*_Lithium_Ion_Integrated_BESS`  (AS/NZS 5139:2019 s4)
 *   systemType 'pre-assembled' → `*_Lithium_Ion_Preassembled_BS`  (s5)
 *   systemType 'other'         → s6; block not yet probed
 *
 *   make            → Make_Lithium_Ion_*
 *   model           → Model_Lithium_Ion_*
 *   serial          → Serial_Number_Lithium_Ion_*
 *   capacityKwh     → Total_Battery_Storage_Capacity_kWh_*
 *   installLocation → Location_Lithium_Ion_*
 *   inverterSerial  → Serial_Number_Inverter_Battery_System_Preassembled_BS (s5 only)
 */
export const CerBatteryEntrySchema = BatteryEntrySchema.extend({
    /** Picks which of the portal's two mutually-exclusive hardware blocks to fill. */
    systemType: BessSystemTypeSchema.optional(),
    /**
     * Free text — the portal asks "Where is the … installed?" and accepts any
     * string. Deliberately NOT the `'internal' | 'external'` enum used by
     * {@link ItpBatteryEntrySchema}: that enum cannot express what this field is
     * asking for, and writing "internal" into it would lose information the
     * inspector recorded.
     */
    installLocation: z.string().optional(),
    /** Serial of the inverter the battery system connects to. Pre-assembled BS only. */
    inverterSerial: z.string().optional(),
    /**
     * Decisive Voltage Classification. Sourced from clause `Pre-assembled BS 14`.
     *
     * ⚠️ The CER portal has no DVC field on the Hardware page — it asks for DVC
     * in **Non-Checklist row 1**, which is a free-text `textarea`. The portal
     * accepts any string there, so a format mismatch will not error, it will just
     * record something CER did not expect. The exact expected wording is still
     * unconfirmed; see `docs/CER_PORTAL_BATTERY.md` ("Still to capture").
     */
    dvcClassification: DvcClassificationSchema.optional(),
});
/**
 * Battery hardware (CER variant).
 *
 * The four system-level fields below are entered on the dashboard (the field
 * app does not capture them). Breaker sizes are normalised to the portal's
 * digits-only pattern before fill.
 */
export const CerBatteryHardwareSchema = z.object({
    batteries: z.array(CerBatteryEntrySchema).optional(),
    /** Inverter rows — battery uses brand/model/serial/quantity only. */
    inverters: z.array(PvInverterEntrySchema).optional(),
    /** → `Circuit_Breaker_Size_Main_Switch_Grid`. Dashboard entry. */
    circuitBreakerSizeMainSwitchGrid: z.string().optional(),
    /** → `Circuit_Breaker_Size_Main_Switch_Inverter_Alternative`. Dashboard entry. */
    circuitBreakerSizeMainSwitchInverter: z.string().optional(),
    /** → `Alternative_Supply_From_BESS_Inverter_Flag`. Dashboard entry. */
    alternativeSupplyFromBessInverter: z.boolean().optional(),
    /** → `Switchboard_Labelling_Requirements_Met`. Dashboard entry. */
    switchboardLabellingRequirementsMet: z.boolean().optional(),
    // TODO: BMS, enclosure, emergency isolation, compliance standard...
});
/** PV hardware (ITP variant). Likely to track CER closely; kept separate to allow drift. */
export const ItpPvHardwareSchema = CerPvHardwareSchema;
/**
 * One T47 audit response. Mirrors the ten response columns in the ITP T47
 * BESS Inspection Checklist (May 2026 spec). Per question, only a subset of
 * these is valid — the X-marks in the source xlsx encode that. The full
 * spec, with per-clause validity, lives in {@link './t47-spec.js'}; use
 * {@link isValidT47Response} to gate inputs against it. The wire schema here
 * stays permissive; portal-side validation is the last line of defence.
 *
 * Display-text mapping (e.g. `'rectification-required'` →
 * `'Rectification work required'`) lives in `t47-spec.ts` as
 * {@link itpAuditResponseDisplayText}.
 */
export const ItpAuditResponseSchema = z.enum([
    'not-sighted',
    'not-applicable',
    'compliant',
    'minor-non-compliance',
    'medium-non-compliance',
    'rectification-required',
    'unsafe',
    'unsafe-product-recall',
    'information-only',
    'non-regulatory',
]);
/** A single ITP audit entry — clauseId + a strict ITP response enum. */
export const ItpAuditEntrySchema = z.object({
    clauseId: z.string(),
    response: ItpAuditResponseSchema.optional(),
    note: z.string().optional(),
});
/**
 * Generic audit entry the parser produces from the CER Battery Checklist PDF.
 * The response is a free-form string (typically `"Yes"` / `"No"` / `"N/A"`) — the
 * content script normalises into portal-specific values when filling forms.
 *
 * Used at payload level for both CER and ITP variants. ITP-specific normalisation
 * (mapping `"No"` → one of the 10 T47 non-compliance levels) happens in the ITP
 * content script, not here.
 */
/**
 * One photo the extension can fetch and attach to a portal upload input.
 *
 * Deliberately NOT {@link PhotoReferenceSchema}: that describes a file sitting
 * in Dropbox for a human to go and find, and requires a `dropboxPath` that is
 * meaningless here. This is an approved-payload photo whose bytes the backend
 * serves from `GET /api/job/:jobId/attachment/photo/:photoId`, so `photoId` is
 * the field that does the work.
 */
export const PortalEvidencePhotoSchema = z.object({
    photoId: z.string(),
    filename: z.string(),
});
export const PortalAuditEntrySchema = z.object({
    clauseId: z.string(),
    response: z.string().optional(),
    note: z.string().optional(),
    /**
     * Clause evidence for this row. The CER portal renders an upload input on
     * every clause row, and the clause is the only thing that says which row a
     * shot belongs on — so evidence rides on the entry rather than in a flat
     * list the uploader would have to re-derive coordinates from.
     *
     * Absent before this existed: `adaptApprovedPayload` built entries without
     * it, which is why the approved payload's photos never reached any fill path.
     */
    photos: z.array(PortalEvidencePhotoSchema).optional(),
    /**
     * Internal assessment value — `compliant`, `medium_non_compliance`,
     * `rectification_work` and so on. Carries the SEVERITY too: the portal has a
     * single per-clause select, so "Medium non-compliance" is both the outcome
     * and its severity, not two controls.
     *
     * Preferred over `response` when filling. `response` is the human answer
     * ("Compliant" / "Non-Compliant"), which cannot express which severity a
     * non-compliance is, and the payload's own `severity` label
     * ("Medium Non Compliance") differs from the portal's option text
     * ("Medium non-compliance") by more than case. Only this value round-trips
     * safely, via `cerPortalOptionText`.
     */
    assessment: z.string().optional(),
});
/**
 * Optional human-readable diagnostics the backend emits alongside the
 * payload so the popup can explain to inspectors WHY parts of the form
 * didn't fill (e.g. "no checklist PDF in folder, audit will be empty").
 *
 * Strings are written for the inspector, not the engineer — they appear
 * verbatim in the side panel below the fill count.
 */
export const PayloadDiagnosticsSchema = z.object({
    warnings: z.array(z.string()).optional(),
});
/** Reference to a photo in Dropbox; used by both portals for evidence upload. */
export const PhotoReferenceSchema = z.object({
    /** Display label (e.g. "Inverter label and serial number"). Maps to ITP's photo evidence list. */
    category: z.string().optional(),
    /** Filename as it sits in Dropbox. */
    filename: z.string(),
    /** Dropbox absolute path. */
    dropboxPath: z.string(),
    /** ISO timestamp from file metadata. */
    takenAt: z.string().optional(),
});
/**
 * One ITP battery row — extends the shared {@link BatteryEntrySchema} with
 * T47-specific taxonomy fields. Field-by-field mapping to the T47 spec
 * (`t47-spec.ts`):
 *
 *   make              ← Integrated BESS 7 / Pre-assembled BS 7
 *   model             ← Integrated BESS 8 / Pre-assembled BS 8
 *   serial            ← Integrated BESS 9 / Pre-assembled BS 10
 *   capacityKwh       ← Integrated BESS 10 / Pre-assembled BS 11
 *   inverterSerial    ← Pre-assembled BS 9 (only on Pre-assembled systems)
 *   installLocation   ← Integrated BESS 6 / Pre-assembled BS 6
 *   systemType        ← BG 3 — picks which subset of clauses applies
 *   dvcClassification ← Pre-assembled BS 14
 *
 * Which fields are populated depends on `systemType` — `integrated` BESS
 * uses the `Integrated BESS *` clauses; `pre-assembled` uses the
 * `Pre-assembled BS *` clauses (and `dvcClassification` + `inverterSerial`
 * become relevant); `other` falls back to free-form serial-only entry.
 */
export const ItpBatteryEntrySchema = BatteryEntrySchema.extend({
    systemType: BessSystemTypeSchema.optional(),
    dvcClassification: DvcClassificationSchema.optional(),
    installLocation: z.enum(['internal', 'external']).optional(),
    inverterSerial: z.string().optional(),
});
/** ITP battery hardware — array of ITP-extended battery rows. */
export const ItpBatteryHardwareSchema = z.object({
    batteries: z.array(ItpBatteryEntrySchema).optional(),
});
/**
 * File paths the popup may want to surface (e.g. "Open consent PDF" / "Open filled checklist").
 * All paths are Dropbox-absolute; the extension never sees them as URLs — it requests file
 * downloads through the backend if needed.
 */
export const PortalArtefactsSchema = z.object({
    /** Filled inspector checklist PDF (CER Battery Checklist with " done" suffix). */
    filledChecklistPath: z.string().optional(),
    /** Customer-signed consent PDF. */
    signedConsentPath: z.string().optional(),
    /** Inspector who performed the audit (parsed from Dropbox folder structure). */
    inspector: z.string().optional(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * Portal payload — single discriminated union over portal+type pairs.
 *
 * `kind` is flat (cer-pv | cer-battery | itp-pv | itp-battery) so TS narrows
 * directly to the correct hardware shape with one switch.
 * ──────────────────────────────────────────────────────────────────────────── */
export const PortalPayloadKindSchema = z.enum(['cer-pv', 'cer-battery', 'itp-pv', 'itp-battery']);
export const PortalPayloadSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('cer-pv'),
        cover: InspectionCoverSheetSchema,
        hardware: CerPvHardwareSchema,
        photos: z.array(PhotoReferenceSchema).optional(),
        audit: z.array(PortalAuditEntrySchema).optional(),
        artefacts: PortalArtefactsSchema.optional(),
        diagnostics: PayloadDiagnosticsSchema.optional(),
    }),
    z.object({
        kind: z.literal('cer-battery'),
        cover: InspectionCoverSheetSchema,
        hardware: CerBatteryHardwareSchema,
        photos: z.array(PhotoReferenceSchema).optional(),
        audit: z.array(PortalAuditEntrySchema).optional(),
        artefacts: PortalArtefactsSchema.optional(),
        diagnostics: PayloadDiagnosticsSchema.optional(),
    }),
    z.object({
        kind: z.literal('itp-pv'),
        cover: InspectionCoverSheetSchema,
        hardware: ItpPvHardwareSchema,
        photos: z.array(PhotoReferenceSchema).optional(),
        audit: z.array(PortalAuditEntrySchema).optional(),
        artefacts: PortalArtefactsSchema.optional(),
        diagnostics: PayloadDiagnosticsSchema.optional(),
    }),
    z.object({
        kind: z.literal('itp-battery'),
        cover: InspectionCoverSheetSchema,
        hardware: ItpBatteryHardwareSchema,
        photos: z.array(PhotoReferenceSchema).optional(),
        audit: z.array(PortalAuditEntrySchema).optional(),
        artefacts: PortalArtefactsSchema.optional(),
        diagnostics: PayloadDiagnosticsSchema.optional(),
    }),
]);
/** Wrapper the backend sends. `ok: true` on success, `ok: false` on a handled failure. */
export const JobLookupResponseSchema = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        payload: PortalPayloadSchema,
    }),
    z.object({
        ok: z.literal(false),
        error: z.object({
            code: z.enum([
                'NOT_FOUND',
                'SM8_ERROR',
                'DROPBOX_ERROR',
                'INVALID_JOB_ID',
                'UNAUTHORIZED',
                'INTERNAL',
            ]),
            message: z.string(),
        }),
    }),
]);
//# sourceMappingURL=cer-portal.js.map